import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '@/config/api';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Eye, Archive, Plus, RotateCcw, Store } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const API = API_BASE_URL;

const Restaurants = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [restaurants, setRestaurants] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [openAdd, setOpenAdd] = useState(false);
  const [view, setView] = useState('active');
  const [detailsRestaurant, setDetailsRestaurant] = useState(null);
  const [actionTarget, setActionTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [form, setForm] = useState({ name: '', company_id: '', address: '', phone: '', whatsapp_number: '' });
  const canManageArchive = user?.role === 'super_admin' || user?.role === 'company_admin';

  const fetchData = useCallback(async () => {
    try {
      const config = { withCredentials: true };
      if (view === 'archived') {
        config.params = { include_archived: true };
      }
      const { data } = await axios.get(`${API}/restaurants`, config);
      setRestaurants(data);
      
      if (user?.role === 'super_admin') {
        const compRes = await axios.get(`${API}/companies`, { withCredentials: true });
        setCompanies(compRes.data);
      }
    } catch (error) {
      console.error(error);
    }
  }, [user?.role, view]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAdd = async () => {
    try {
      const payload = { ...form };
      // company_admin: backend auto-assigns; super_admin: must provide
      if (user?.role === 'company_admin') {
        delete payload.company_id;
      } else if (!form.company_id) {
        toast.error('Please select a company');
        return;
      }
      await axios.post(`${API}/restaurants`, payload, { withCredentials: true });
      toast.success('Restaurant added');
      setOpenAdd(false);
      setForm({ name: '', company_id: '', address: '', phone: '', whatsapp_number: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add');
    }
  };

  const handleArchiveAction = async () => {
    if (!actionTarget) return;
    setActionLoading(true);
    const isRestore = actionTarget.action === 'restore';
    try {
      await axios.patch(
        `${API}/restaurants/${actionTarget.restaurant.id}/${isRestore ? 'restore' : 'archive'}`,
        {},
        { withCredentials: true },
      );
      toast.success(isRestore ? 'Restaurant restored' : 'Restaurant archived');
      setActionTarget(null);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${isRestore ? 'restore' : 'archive'} restaurant`);
    } finally {
      setActionLoading(false);
    }
  };

  const statusLabel = (restaurant) => restaurant.status === 'archived' ? 'Archived' : 'Active';

  return (
    <div className="space-y-6" data-testid="restaurants-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: '#232D4B' }}>
          {t('restaurants')}
        </h1>
        <div className="flex items-center gap-2">
          {canManageArchive && (
            <div className="flex rounded-md border border-border p-1" role="group" aria-label="Restaurant status filter">
              <Button type="button" variant={view === 'active' ? 'secondary' : 'ghost'} size="sm" onClick={() => setView('active')} data-testid="active-restaurants-filter">Active</Button>
              <Button type="button" variant={view === 'archived' ? 'secondary' : 'ghost'} size="sm" onClick={() => setView('archived')} data-testid="archived-restaurants-filter">Archived</Button>
            </div>
          )}
          {canManageArchive && view === 'active' && (
            <Dialog open={openAdd} onOpenChange={setOpenAdd}>
              <DialogTrigger asChild>
                <Button style={{ backgroundColor: '#E57200' }} data-testid="add-restaurant-btn">
                  <Plus className="h-4 w-4 me-2" /> Add Restaurant
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Restaurant</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div><Label>Restaurant Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="rest-name-input" /></div>
                  {user?.role === 'super_admin' && (
                    <div>
                      <Label>Company</Label>
                      <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                        <SelectTrigger><SelectValue placeholder="Select company" /></SelectTrigger>
                        <SelectContent>
                          {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                  <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                  <div><Label>WhatsApp Number</Label><Input value={form.whatsapp_number} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })} /></div>
                </div>
                <DialogFooter>
                  <Button onClick={handleAdd} style={{ backgroundColor: '#E57200' }} data-testid="save-rest-btn">Save</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <Card className="border-border/40">
        <CardContent className="pt-6">
          <Table data-testid="restaurants-table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>WhatsApp</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-end">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {restaurants.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">No restaurants in this view</TableCell></TableRow>
              ) : (
                restaurants.map(r => (
                  <TableRow key={r.id} className="hover:bg-muted/50">
                    <TableCell className="font-medium flex items-center gap-2">
                      <Store className="h-4 w-4" style={{ color: '#E57200' }} />
                      {r.name}
                    </TableCell>
                    <TableCell>{r.phone || '-'}</TableCell>
                    <TableCell>{r.whatsapp_number || '-'}</TableCell>
                    <TableCell>{r.address || '-'}</TableCell>
                    <TableCell>
                      <Badge variant={r.status === 'archived' ? 'secondary' : 'default'}>{statusLabel(r)}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={() => setDetailsRestaurant(r)} data-testid={`view-details-${r.id}`}>
                          <Eye className="h-4 w-4 me-2" /> View details
                        </Button>
                        {canManageArchive && r.status === 'archived' && (
                          <Button type="button" variant="outline" size="sm" onClick={() => setActionTarget({ restaurant: r, action: 'restore' })} data-testid={`restore-restaurant-${r.id}`}>
                            <RotateCcw className="h-4 w-4 me-2" /> Restore
                          </Button>
                        )}
                        {canManageArchive && r.status !== 'archived' && (
                          <Button type="button" variant="outline" size="sm" onClick={() => setActionTarget({ restaurant: r, action: 'archive' })} data-testid={`archive-restaurant-${r.id}`}>
                            <Archive className="h-4 w-4 me-2" /> Archive
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={Boolean(detailsRestaurant)} onOpenChange={(open) => !open && setDetailsRestaurant(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{detailsRestaurant?.name || 'Restaurant details'}</DialogTitle>
          </DialogHeader>
          {detailsRestaurant && (
            <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-3 text-sm">
              <dt className="font-medium text-muted-foreground">Status</dt><dd><Badge variant={detailsRestaurant.status === 'archived' ? 'secondary' : 'default'}>{statusLabel(detailsRestaurant)}</Badge></dd>
              <dt className="font-medium text-muted-foreground">Phone</dt><dd>{detailsRestaurant.phone || '-'}</dd>
              <dt className="font-medium text-muted-foreground">WhatsApp</dt><dd>{detailsRestaurant.whatsapp_number || '-'}</dd>
              <dt className="font-medium text-muted-foreground">Address</dt><dd>{detailsRestaurant.address || '-'}</dd>
              <dt className="font-medium text-muted-foreground">Currency</dt><dd>{detailsRestaurant.currency || 'USD'}</dd>
              <dt className="font-medium text-muted-foreground">Created</dt><dd>{detailsRestaurant.created_at || '-'}</dd>
              {detailsRestaurant.archived_at && <><dt className="font-medium text-muted-foreground">Archived</dt><dd>{detailsRestaurant.archived_at}</dd></>}
            </dl>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(actionTarget)} onOpenChange={(open) => !open && !actionLoading && setActionTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{actionTarget?.action === 'restore' ? 'Restore restaurant?' : 'Archive restaurant?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {actionTarget?.action === 'restore'
                ? `Restore ${actionTarget?.restaurant.name} to the active restaurant list?`
                : `Archiving ${actionTarget?.restaurant.name} disables operational use while preserving its historical data. This is not permanent deletion.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(event) => { event.preventDefault(); void handleArchiveAction(); }} disabled={actionLoading}>
              {actionTarget?.action === 'restore' ? 'Restore' : 'Archive'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Restaurants;
